import type { TFunction } from 'i18next'

/**
 * Every Birdie string that is NOT a marble-colour option label, exposed as a
 * literal `t()` call with the English sentence spelled out in full.
 *
 * A25: the i18n extractor is literal-only — a key exists for `i18n:sync` and
 * survives `i18n:cleanup` only if the exact string appears, quoted, as the
 * first argument of a `t()` call in a non-test source file. The Birdie board
 * (`BirdieBoard`) and the golfer card land in a later task, so without this
 * module the copy written into the 16 locale files in this task would be
 * invisible to the tooling and the next
 * `pnpm run i18n:cleanup` would delete all 16 translations of it.
 *
 * It lives here, next to the other Birdie-owned helpers, rather than in the
 * shared `ChipPlacementPanel` — Birdie gets its own board component and the
 * shared panel must not grow Birdie-only literals.
 *
 * The three `toast.errors.*` reject reasons are NOT here: the extractor picks
 * those up from the `message:` literals in `store/ui.ts`, alongside every
 * other bet-rejection toast.
 */
export const birdieBoardCopy = (t: TFunction) => ({
  /** Go never opened the round, or the market is closed (A34/A68). */
  bettingUnavailable: t('Betting unavailable this round'),
  /** race_ready has not landed yet, so no option can be priced. */
  waitingForOdds: t('Waiting for this round’s odds'),
  /** Under the per-option make-chance percentage on each pill. */
  makeChanceCaption: t('make chance per putt'),
  /** The green is part of the reveal — it is not shown while betting. */
  greenHiddenUntilClip: t('The green is shown when the clip starts')
})

/**
 * The golfer card: handicap, hole number, putt distance and the three
 * shorthand shape labels the card uses to describe this round's odds.
 */
/**
 * The card shows the putt in FEET (owner 2026-09-21: "we show 3 m on the game ui, should probably be
 * 3 ft") — putting lengths are quoted in feet everywhere in golf. The wire stays metres
 * (`distance_m` on the odds card and the venue), converted once here.
 */
export const feetFromMetres = (metres: number): number => Math.round(metres / 0.3048)

export const birdieGolferCardCopy = (
  t: TFunction,
  card: { handicap: string | number; hole: string | number; distance: string | number }
) => ({
  title: t('Golfer card'),
  handicap: t('HCP {{handicap}}', { handicap: card.handicap }),
  hole: t('Hole {{hole}}', { hole: card.hole }),
  distance: t('{{distance}} ft', { distance: card.distance }),
  level: t('Level'),
  greenDifficulty: t('Green difficulty'),
  favourite: t('Favourite'),
  balanced: t('Balanced'),
  longshot: t('Longshot')
})

/**
 * The settled capsule's headline (B16): how many of the round's three putts went
 * in, in a large gold treatment where the stat bars were.
 *
 * ONE key, not a singular/plural pair like `birdieChipsClampedCopy` below: the
 * locale files are keyed by the literal English sentence and English says "1 of
 * 3 in" and "2 of 3 in" with the same words, so a second English literal would
 * be the same key. A locale that needs a different form for one has to phrase
 * around it inside its single translation (`scripts/birdieLocales.mjs`).
 */
export const birdieFinalScoreCopy = (t: TFunction, count: number): string =>
  t('{{count}} of 3 in', { count })

/**
 * The past-rounds strip and its round sheet (B17).
 *
 * Four literals are NEW to Birdie and owed sixteen translations
 * (`scripts/birdieLocales.mjs` + the gate). The rest are shared widget strings
 * that already ship translated — `Provably fair`, `Chain index`, `Chain root`,
 * `Result`, `Round`, `Copy`, `Copied!` and `Close` — reused verbatim rather
 * than re-minted, which is eight keys and 128 translations not written. They
 * are blank in `en-soc` only, and that is filled the same way the board's other
 * borrowed strings are.
 *
 * `Server seed hash`, `Revealed seed`, `Paytable hash`, `Catalogue` and `Clip`
 * are new because no existing key spells them out: the crash sheet labels its
 * commitment differently and has no catalogue or clip of its own.
 */
export const birdiePastRoundsCopy = (t: TFunction) => ({
  /** The disc rail's accessible name (B19b), and the round list's heading. */
  title: t('Past rounds'),
  /** Opens the sheet's row; the visible columns carry the numbers. */
  roundDetails: t('Round details'),
  /** The sheet header's eyebrow, beside the round number. */
  round: t('Round'),
  close: t('Close'),
  /** The three putts of that round. */
  result: t('Result'),
  /** The two winning options and the prices they paid. */
  whatPaid: t('What paid'),
  provablyFair: t('Provably fair'),
  serverSeedHash: t('Server seed hash'),
  revealedSeed: t('Revealed seed'),
  chainRoot: t('Chain root'),
  chainIndex: t('Chain index'),
  paytableHash: t('Paytable hash'),
  catalogue: t('Catalogue'),
  clip: t('Clip'),
  copy: t('Copy'),
  copied: t('Copied!'),
  showMore: t('Show more'),
  verify: t('Verify this round')
})

/**
 * The round CARD and its modal (B19).
 *
 * Five literals are new; everything else is borrowed. The two market names are
 * the board's own column headings (and the forecast games' bet-type labels
 * before that), the Lightning mark is the platform's existing name for a
 * boosted round, the round, close, provably-fair and verify labels come from
 * B17's sheet, and the relative-time strings are the crash fairness modal's
 * (`crashRoundFairness.formatTimeAgo`), already translated in all sixteen
 * locales.
 *
 * The five new ones exist because nothing in the widget says them:
 *  - the pattern line on a round that has no bettable exact option (HHH / MMM
 *    are settled but never priced, so those rounds have ONE winner);
 *  - the two lightning tags (a winner that was boosted, and a boosted option
 *    that did not come in);
 *  - the verify pill's hint;
 *  - `Card draw`, the one reveal value the Birdie engine publishes that B17's
 *    sheet never labelled.
 */
export const birdieRoundCardCopy = (t: TFunction) => ({
  /** HHH and MMM settle but are not bettable, so those rounds pay one option. */
  noExactPattern: t('no exact pattern for this count'),
  /** The two market tags under a winner tile. */
  exactOrder: t('Exact order'),
  anyOrder: t('Any order'),
  /** B36: the round-type pair names itself on a settled card — the option IS the market. */
  frostRound: t('Frost round'),
  fireRound: t('Fire round'),
  /** The eyebrow's mark on a boosted round, and the seal's own name. */
  lightning: t('Lightning Round'),
  /** A winner tile that carried the boost: "Any order · lightning". */
  lightningTag: (market: string) => t('{{market}} · lightning', { market }),
  /** …and the third tile, for a boost that landed on nothing. */
  lightningMissed: t('Lightning was on this option · it did not come in'),
  /** Under the verify pill, so the new tab is never a surprise. */
  verifyHint: t('Opens the public verifier with this round filled in'),
  /** The card derivation's own draw — Birdie's, not the pattern draw's. */
  cardDraw: t('Card draw'),
  /**
   * The caption under the card's make-chance figure — the mockup's two words,
   * NOT the board tile's `make chance per putt`.
   *
   * The board has a whole pill to caption and can afford the longer sentence;
   * the card's caption sits in a fixed box beside a 24-px figure, and the long
   * one needed three lines there (measured: 17 px tall against a 26 px scroll
   * height), so every card in both densities showed "MAKE / CHANCE PE…". One
   * key against sixteen translations, and the figure reads properly.
   */
  makeChance: t('make chance')
})

/**
 * The recent-form panel (B19 Task 2) — `<RoundStats>`' Birdie branch since B19b.
 *
 * TWO new literals in the whole panel, and both are in its footer. Everything
 * else it says is already translated in all sixteen locales and is borrowed
 * rather than re-minted:
 *  - `Recent form` and `Last {{count}} rounds` are the OTHER games' round-stats
 *    panel (`RoundStats.tsx`), which is the point — a player who knows those
 *    rows reads these at once;
 *  - the four row names (`None in` … `All three in`) are not strings at all
 *    here: they are the count market's `marbleColors` keys, so the panel and
 *    the board's own bars can never name an outcome differently.
 *
 * The two that are new exist because nothing in the widget says them: Birdie is
 * the only game whose rounds carry a per-round make chance, and "Biggest" on
 * its own (the crash panel's word) does not say what the number is.
 *
 * B19b dropped the pager entry: the history list is `<PastRacesSidebar>`'s
 * again, and its own `t('Show more')` is the key the locales already carry.
 */
export const birdieHistoryCopy = (t: TFunction) => ({
  /** The panel's own heading, and its accessible name. */
  recentForm: t('Recent form'),
  /** The window line, right of the heading. */
  window: (count: number) => t('Last {{count}} rounds', { count }),
  /** Footer left: the mean make chance over the window. */
  avgMakeChance: t('Avg make chance'),
  /** Footer right, in gold: the biggest price that paid in the window. */
  biggestPaid: t('Biggest paid')
})

/**
 * "how long ago", from the four keys the crash fairness modal already ships in
 * sixteen locales (`crashRoundFairness.ts:598-609`).
 *
 * Takes the elapsed milliseconds rather than reading the clock, so the card's
 * model stays pure and one clock drives every row on screen.
 */
export const birdieTimeAgo = (t: TFunction, elapsedMs: number): string => {
  const seconds = Math.max(0, Math.floor(elapsedMs / 1000))
  if (seconds < 10) return t('Just now')
  if (seconds < 60) return t('{{count}}s ago', { count: seconds })
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return t('{{count}}m ago', { count: minutes })
  return t('{{count}}h ago', { count: Math.floor(minutes / 60) })
}

/**
 * Birdie's per-option max stake moves with the round's odds, so chips already
 * on the board can be cut back when a new board arrives. i18next's own plural
 * suffixes are not used here: the locale files are keyed by the literal
 * English sentence, so the singular and the plural are two separate keys.
 */
export const birdieChipsClampedCopy = (t: TFunction, count: number): string =>
  count === 1
    ? t('{{count}} chip reduced to this round’s max', { count })
    : t('{{count}} chips reduced to this round’s max', { count })

/**
 * The handicap as golfers write it: a plus handicap (better than scratch) is carried negative on the
 * wire and reads "+1"; none reads as nothing.
 */
export const handicapFigure = (handicap: number | undefined): string | number =>
  handicap === undefined ? '' : handicap < 0 ? `+${-handicap}` : handicap
